/**
 * ThemeLoading custom element for showing loading states
 * 
 * @module theme-loading
 * @version 1.0.0
 * @extends HTMLElement
 */

if (!customElements.get('theme-loading')) {
  
  class ThemeLoading extends HTMLElement {

    /**
     * Constructor for the ThemeLoading element
     */
    constructor() {
      super();
      
      // Add styles immediately since they're global
      this.addStyles();
    }

    /**
     * Connected callback - runs when element is added to DOM
     */
    connectedCallback() {

      // Render when connected to DOM
      this.render();
      
      // Start hidden by default
      this.disable();
    }

    /**
     * Add necessary styles for the loading component
     */
    addStyles() {
      // Only add styles once
      if (!document.getElementById('theme-loading-styles')) {
        const style = document.createElement('style');
        style.id = 'theme-loading-styles';
        style.textContent = `
          body.loading {
            overflow: hidden !important;
          }
          
          .pageloader {
            backdrop-filter: blur(2px);
          }
          
          @keyframes pulse {
            0%, 100% { opacity: 1; }
            50% { opacity: 0.7; }
          }
          
          .pageloader svg {
            animation: pulse 2s cubic-bezier(0.4, 0, 0.6, 1) infinite;
          }
        `;
        document.head.appendChild(style);
      }
    }

    /**
     * Render the loading component structure
     */
    render() {
      this.innerHTML = `
        <div class="after:-z-10 after:absolute after:bg-white after:bottom-0 after:content-[''] after:left-0 after:opacity-50 after:right-0 after:top-0 after:w-full bottom-0 fixed hidden items-center justify-center left-0 right-0 top-0 pageloader z-50">
          <div class="loader-logo relative flex items-center justify-center w-14 h-11">
            <svg width="22" height="24" aria-hidden="true" role="img" viewBox="0 0 28 30" fill="none" xmlns="http://www.w3.org/2000/svg">
              <path d="M8.92784 17.9146L7.49213 21.2488H14.1838L15.6195 17.9146H8.92784Z" fill="#BBA2AA"></path>
              <path d="M7.4921 21.2486L3.96883 29.5884H0L3.69654 21.2486H7.4921ZM13.598 8.05159L8.92781 17.9144H5.17351L13.598 0L27.5343 29.5884H23.5655L13.598 8.05159Z" fill="#231F20"></path>
            </svg>
            <div class="absolute top-0 w-12 h-12 rounded-full animate-spin border-2 border-dashed border-gray-400 border-t-transparent"></div>
          </div>
        </div>
      `;
    }

    /**
     * Enable the loading state - show the loader
     */
    enable() {

      const loader = this.querySelector('.pageloader');

      if (loader) {
        loader.classList.remove('hidden');
        loader.classList.add('flex');
      }
      
      // Add loading class to body to prevent scrolling
      document.body.classList.add('loading');
    }

    /**
     * Disable the loading state - hide the loader
     */
    disable() {

      const loader = this.querySelector('.pageloader');

      if (loader) {
        loader.classList.add('hidden');
        loader.classList.remove('flex');
      }
      
      // Remove loading class from body
      document.body.classList.remove('loading');
    }

    /**
     * Toggle the loading state
     */
    toggle() {

      const loader = this.querySelector('.pageloader');

      if (loader && loader.classList.contains('hidden')) {
        this.enable();
      } else {
        this.disable();
      }
    }

    /**
     * Check if loading is currently enabled
     * 
     * @returns {boolean} True if loading is enabled
     */
    isEnabled() {
      const loader = this.querySelector('.pageloader');
      return loader && !loader.classList.contains('hidden');
    }
  }

  // Define the custom element
  customElements.define('theme-loading', ThemeLoading);

  // Create global static methods for easy access
  class ThemeLoader {

    static instance = null;
    
    static async getInstance() {

      if (!this.instance) {

        // Ensure custom element is defined
        if (!customElements.get('theme-loading')) {
          await customElements.whenDefined('theme-loading');
        }
        
        this.instance = document.createElement('theme-loading');
        
        // Ensure DOM is ready before appending
        if (document.body) {

          document.body.appendChild(this.instance);

        } else {

          document.addEventListener('DOMContentLoaded', () => {
            document.body.appendChild(this.instance);
          });
        }
      }
      
      return this.instance;
    }
    
    static async enable() {
      const instance = await this.getInstance();
      instance.enable();
    }
    
    static async disable() {
      const instance = await this.getInstance();
      instance.disable();
    }
    
    static async toggle() {
      const instance = await this.getInstance();
      instance.toggle();
    }
    
    static async isEnabled() {
      const instance = await this.getInstance();
      return instance.isEnabled();
    }
  }

  // Initialize when ready
  function initializeThemeLoading() {
    window.theme = window.theme || {};
    window.theme.ThemeLoader = ThemeLoader;
    
    // Create instance asynchronously
    ThemeLoader.getInstance().then(instance => {
      window.theme.loading = instance;
    });
  }

  // Initialize when document and custom elements are ready
  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', initializeThemeLoading);
  } else {
    initializeThemeLoading();
  }

}